import {
  Button,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { ReactNode } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { errorMessageFor } from 'utils/forms.utils';
import { handleFocusOtp } from 'utils/mui-one-time-code.utils';
import { KeyboardPaddingBottom } from 'utils/styles.utils';
import { postMessageRN } from 'utils/webview.utils';

import {
  SAUDI_REWARDS_FORGOT_PIN_URL,
  SAUDI_REWARDS_URL,
} from 'constants/saudi-rewards.constants';
import { VALIDATIONS } from 'constants/validation.constants';
import { useEmbedded } from 'hooks/useEmbedded';
import { useLinkSaudiRewardsAccount } from 'restful/accounts';
import { useTrackSaudiRewardsLinked } from 'services/datalayer/account';

import { SaudiRewardsFormFields } from 'components/@account/types';
import BorderStack from 'components/@common/BorderStack';
import Dialog from 'components/@common/Dialog';
import Link from 'components/@common/Link';
import Form from 'components/@forms/Form';
import FormOTPField from 'components/@forms/FormOTPField';
import FormTextField from 'components/@forms/FormTextField';

interface Props {
  open: boolean;
  onClose: () => void;
}

const SaudiRewardsLinkAccountModal = ({ open, onClose }: Props) => {
  const intl = useIntl();
  const { linkSaudiRewardsAccount, isLoading } = useLinkSaudiRewardsAccount();
  const { enqueueSnackbar } = useSnackbar();
  const { isEmbedded } = useEmbedded();
  const trackSaudiRewardsLinked = useTrackSaudiRewardsLinked();

  const handleSubmit: SubmitHandler<SaudiRewardsFormFields> = (data) => {
    linkSaudiRewardsAccount(
      {
        memberId: data.saudiRewardsMemberId,
        memberPin: data.saudiRewardsMemberPin,
      },
      {
        onSuccess: () => {
          trackSaudiRewardsLinked();
          enqueueSnackbar(
            intl.formatMessage({ id: 'saudi.rewards.link.success.alert' }),
            { variant: 'success' },
          );
          onClose();
        },
        onError: () => {
          enqueueSnackbar(
            intl.formatMessage({ id: 'api.error.saudi.rewards' }),
            { variant: 'error' },
          );
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        <FormattedMessage id="saudi.rewards.link.account.title" />
      </DialogTitle>
      <DialogContent sx={KeyboardPaddingBottom}>
        <Typography
          variant="small"
          sx={{
            color: 'text.secondary',
            mb: 2,
          }}
        >
          <FormattedMessage
            id="saudi.rewards.link.form.title"
            values={{
              a: (chunks: Array<ReactNode>) => {
                if (isEmbedded) {
                  return (
                    <Link
                      component={Button}
                      onClick={() => postMessageRN({ href: SAUDI_REWARDS_URL })}
                      underline="always"
                      variant="small"
                      color="inherit"
                    >
                      {chunks}
                    </Link>
                  );
                }
                return (
                  <Link
                    href={SAUDI_REWARDS_URL}
                    target="_blank"
                    underline="always"
                    variant="small"
                    color="inherit"
                  >
                    {chunks}
                  </Link>
                );
              },
            }}
          />
        </Typography>
        <Form
          onSubmit={handleSubmit}
          defaultValues={{
            saudiRewardsMemberId: '',
            saudiRewardsMemberPin: '',
          }}
        >
          <Stack
            sx={{
              gap: 1,
              mb: 2,
            }}
          >
            <FormTextField
              label={<FormattedMessage id="form.label.member_id" />}
              name="saudiRewardsMemberId"
              rules={{
                required: errorMessageFor(intl, {
                  type: 'required',
                  label: 'member_id',
                }),
                pattern: {
                  value: VALIDATIONS.saudiRewardsMemberId,
                  message: intl.formatMessage({
                    id: 'saudi.rewards.validate.pin.error',
                  }),
                },
              }}
              type="text"
              inputMode="text"
              autoFocus
              onKeyDown={handleFocusOtp}
            />
            <BorderStack sx={{ py: 3, gap: 2 }}>
              <Typography
                variant="small"
                sx={{
                  color: 'text.secondary',
                }}
              >
                <FormattedMessage id="saudi.rewards.link.account.pin.helper.text" />
              </Typography>
              <FormOTPField
                name="saudiRewardsMemberPin"
                rules={{
                  validate: (value) =>
                    value.length === 4
                      ? true
                      : errorMessageFor(intl, {
                          type: 'min_length',
                          label: 'pin',
                          value: 4,
                        }),
                  required: errorMessageFor(intl, {
                    type: 'required',
                    label: 'pin',
                  }),
                }}
                type="password"
                codeLength={4}
              />
              <Button
                variant="outlined"
                color="secondary"
                type="submit"
                loading={isLoading}
                sx={{ width: { md: 'fit-content' } }}
              >
                <FormattedMessage id="saudi.rewards.link.account.button" />
              </Button>
              {isEmbedded ? (
                <Link
                  component={Button}
                  onClick={() =>
                    postMessageRN({ href: SAUDI_REWARDS_FORGOT_PIN_URL })
                  }
                  variant="small"
                  underline="always"
                  color="text.secondary"
                >
                  <FormattedMessage id="saudi.rewards.link.form.forgot.pin" />
                </Link>
              ) : (
                <Link
                  href={SAUDI_REWARDS_FORGOT_PIN_URL}
                  target="_blank"
                  variant="small"
                  underline="always"
                  color="text.secondary"
                >
                  <FormattedMessage id="saudi.rewards.link.form.forgot.pin" />
                </Link>
              )}
            </BorderStack>
          </Stack>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default SaudiRewardsLinkAccountModal;
